import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import { useAuth } from "../lib/auth";
import { Gavel, Flame, ArrowRight } from "lucide-react";

export default function AuctionsPage() {
    const [listings, setListings] = useState([]);
    const [favIds, setFavIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/listings?sale_type=auction&limit=50");
                // sort active first, then by auction_end soonest
                const now = Date.now();
                const sorted = [...data].sort((a, b) => {
                    const aEnd = a.auction_end ? new Date(a.auction_end).getTime() : Infinity;
                    const bEnd = b.auction_end ? new Date(b.auction_end).getTime() : Infinity;
                    const aActive = aEnd > now;
                    const bActive = bEnd > now;
                    if (aActive !== bActive) return aActive ? -1 : 1;
                    return aEnd - bEnd;
                });
                setListings(sorted);
            } finally {
                setLoading(false);
            }
            if (user && user !== false) {
                try {
                    const ids = await api.get("/favorites/ids");
                    setFavIds(ids.data);
                } catch (e) {
                    console.error("Failed to load favorite ids", e);
                }
            }
        })();
    }, [user]);

    const toggleFav = async (id) => {
        if (!user || user === false) {
            window.location.href = "/login";
            return;
        }
        const { data } = await api.post(`/favorites/${id}`);
        setFavIds((prev) => (data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
    };

    const now = Date.now();
    const active = listings.filter((l) => !l.auction_end || new Date(l.auction_end).getTime() > now);
    const ended = listings.filter((l) => l.auction_end && new Date(l.auction_end).getTime() <= now);

    return (
        <div data-testid="auctions-page">
            {/* Hero */}
            <section className="bg-[#111827] text-white relative overflow-hidden">
                <div className="absolute inset-0 yellow-stripe opacity-10" />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
                    <div className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider mb-6">
                        <Gavel className="w-3.5 h-3.5" /> Händler-Auktionen
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight mb-3 leading-[0.95]">
                        Live-<span className="text-[#FFCC00]">Auktionen</span>.
                    </h1>
                    <p className="text-lg text-white/80 max-w-2xl">
                        Aktuelle Auktionen verifizierter Händler. Höchstes Gebot erhält den Zuschlag — optional mit Mindestpreis (Reserve) zum Schutz des Verkäufers.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-6 text-sm">
                        <div className="flex items-center gap-2"><Flame className="w-4 h-4 text-[#FFCC00]" /> <span className="font-mono-num font-bold text-2xl">{active.length}</span> <span className="text-white/60">aktive Auktionen</span></div>
                        <div className="flex items-center gap-2"><Gavel className="w-4 h-4 text-[#FFCC00]" /> <span className="font-mono-num font-bold text-2xl">{listings.reduce((s, l) => s + (l.bid_count || 0), 0)}</span> <span className="text-white/60">Gebote insgesamt</span></div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => <div key={i} className="bg-white border border-gray-200 rounded-sm h-48 animate-pulse" />)}
                    </div>
                ) : (
                    <>
                        <div className="mb-6 flex items-center justify-between">
                            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">Aktive Auktionen ({active.length})</h2>
                            <Link to="/search" className="text-sm font-bold border-b-2 border-[#111827] hover:gap-2 transition-all inline-flex items-center gap-1">
                                Alle Inserate <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        {active.length === 0 ? (
                            <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-sm">
                                <Gavel className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-600">Aktuell keine aktiven Auktionen.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {active.map((l) => (
                                    <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} />
                                ))}
                            </div>
                        )}

                        {ended.length > 0 && (
                            <>
                                <h2 className="font-display text-2xl font-black tracking-tight mt-16 mb-6 text-gray-500">Beendete Auktionen</h2>
                                <div className="space-y-4 opacity-70">
                                    {ended.map((l) => (
                                        <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}
